import dayjs from "dayjs";
import React, { FC, useCallback, useEffect, useState } from "react";
import { useLazyLocateCampaignQuery } from "../../api/campaignAPI";
import Button, { ButtonIntentE, ButtonModeE } from "../../components/Button/Button";
import Form from "../../components/Form/Form";
import { ValidatorsE } from "../../components/Form/yupValidations";
import Loader from "../../components/Loader/Loader";
import { P, Span } from "../../components/Text/Text";
import { CampaignWizardMode, LangE, StatusE } from "../../models/enums";
import { FormFieldT } from "../../models/models";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  selectCurrentLang,
  selectFindCampaignStep,
} from "../../store/selectors/appSelectors";
import { setFindCampaignStep } from "../../store/slices/appSlice";
import { DATE_FORMAT } from "../../utils/constants";
import usePopup from "../../utils/hooks/usePopup";
import { useCampaignWizard } from "../CampaignWizard/useCampaignWizard";
import { useSelectOptions } from "../Wizard/helpers/useSelectOptions";
import { WizardModeE, WizardTypeE } from "../Wizard/models";
import { useWizardCreator } from "../Wizard/useWizardCreator";
import isMoreThan30Days from "./helpers/isMoreThen30Days";

import styles from "./styles.module.scss";

const FindCampaignBody: FC = () => {
  const { createCampaignWizard } = useCampaignWizard();
  const lang = useAppSelector(selectCurrentLang);
  const findCampaignStep = useAppSelector(selectFindCampaignStep);
  const dispatch = useAppDispatch();
  const { brandsSO } = useSelectOptions();
  const [findCampaign, { isFetching, isSuccess }] =
    useLazyLocateCampaignQuery();
  const [foundCampaigns, setFoundCampaigns] = useState([]);
  const { createPopup, removePopup } = usePopup();
  const createWizard = useWizardCreator();
  const fields: FormFieldT[] = [
    {
      label: "Campaign number",
      validation: ValidatorsE.REQUIRED_NUMBER,
      type: "number",
      extraContent: () => (
        <Span className={styles.campaignNumberHint}>
          Make sure to insert a campaign number and not a delivery number. You
          can find the campaign number in the email that was sent to you with
          all the campaign details.
        </Span>
      ),
    },
    { label: "Brand", selectOptions: brandsSO },
  ];

  useEffect(() => {
    dispatch(setFindCampaignStep(1));
  }, []);

  const handleSubmit = async (data: any) => {
    const res: any = await findCampaign({
      campaignId: data.campaignNumber,
      brandId: data.brand?.value,
    });
    setFoundCampaigns(res?.data?.campaings_data);

    if (!res?.data?.campaings_data?.length) {
      dispatch(setFindCampaignStep(1));
    } else {
      dispatch(setFindCampaignStep(2));
    }
  };

  const handleClickNewCampaign = () => {
    removePopup();
    createCampaignWizard("", CampaignWizardMode.CREATE, StatusE.Opened);
  };

  const handleSelectCampaign = useCallback(() => {
    const wizardPopup = createWizard({
      type: WizardTypeE.Delivery,
      mode: WizardModeE.Create,
      campaignDeliveryId: foundCampaigns?.[0].campaing_metadata.FirstDeliveryId,
      deliveryId: foundCampaigns?.[0].campaing_metadata.FirstDeliveryId,
    });
    removePopup();
    createPopup(wizardPopup);
  }, [foundCampaigns]);

  return (
    <div>
      {findCampaignStep === 1 && (
        <>
          <P className={styles.description}>
            Enter the campaign number and select the brand of the campaign to
            which you want to add additional versions.
          </P>
          {!!brandsSO?.length && (
            <Form
              fields={fields}
              onSubmit={handleSubmit}
              formID="find-campaign"
            />
          )}
          {(!brandsSO?.length || isFetching) && (
            <div>
              <Loader />
            </div>
          )}
          {isSuccess && !isFetching && !foundCampaigns?.length && (
            <P>No results found</P>
          )}
        </>
      )}
      {findCampaignStep === 2 && (
        <>
          {isFetching ? (
            <div>
              <Loader />
            </div>
          ) : (
            <ul className={styles.campaignList}>
              {!!foundCampaigns?.length &&
                !isFetching &&
                foundCampaigns.map((item) => (
                  <li className={styles.campaignItem}>
                    <div>
                      <span>{item.CampaignId}</span>
                      <span> - </span>
                      <span>{item.CampaignName}</span>
                    </div>
                    <div>
                      <span>
                        <Span>Advertiser</Span>:{" "}
                        {
                          item.campaing_metadata[
                            lang === LangE.EN
                              ? "AdvertiserNameEN"
                              : "AdvertiserName"
                          ]
                        }
                      </span>
                      <span>
                        <Span>Broadcasting date</Span>:{" "}
                        {dayjs(item.campaing_metadata.BroadcastingDate).format(
                          DATE_FORMAT
                        )}
                      </span>
                      {isMoreThan30Days(
                        item.campaing_metadata.BroadcastingDate
                      ) && (
                        <Button
                          mode={ButtonModeE.OUTLINED}
                          intent={ButtonIntentE.CAMPAIGN}
                          onClick={handleClickNewCampaign}
                        >
                          Create a new campaign
                        </Button>
                      )}
                      <Button onClick={handleSelectCampaign}>
                        Continue with this campaign
                      </Button>
                    </div>
                    <div>
                      {isMoreThan30Days(
                        item.campaing_metadata.BroadcastingDate
                      ) && (
                        <P className={styles.note}>
                          This existing campaign was set for broadcasting more
                          than 30 days ago. Do you still want to add the version
                          to this campaign or create a new campaign? Adding more
                          versions to an existing campaign is intended for the
                          purpose of tracking all the broadcasts of all the
                          versions belonging to the campaign in the report
                          systems of the ranking committee. If this is a
                          separate campaign that is required for statistical
                          monitoring, you need to select the option of creating
                          a new campaign.
                        </P>
                      )}
                    </div>
                  </li>
                ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
};

export default FindCampaignBody;
